import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Send } from "lucide-react";
import api from "@/lib/api";
import { Reveal, HeroLine, FadeIn } from "@/components/Reveal";
import { inputCls, Label, GoldButton } from "@/components/Field";
import { usePageTitle } from "@/hooks/use-page-title";

export const CONTACT = {
  phone: "(612) 913-2888",
  phoneHref: "tel:+16129132888",
  email: "aspirapropertygroup@gmail.com",
  emailHref: "mailto:aspirapropertygroup@gmail.com",
};

const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-aspira-gold">{children}</p>
);

const ContactForm = () => {
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", inquiry_type: "General Question", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads/contact", form);
      toast.success("Message sent", { description: "Thanks for reaching out — we'll be in touch shortly." });
      setForm({ name: "", email: "", phone: "", inquiry_type: "General Question", message: "" });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="contact-form"
      className="rounded-3xl border border-aspira-goldborder bg-white p-7 shadow-xl sm:p-9"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <Label htmlFor="contact-name">Name *</Label>
          <input id="contact-name" data-testid="contact-name" required className={inputCls} placeholder="Jane Doe" value={form.name} onChange={set("name")} />
        </div>
        <div>
          <Label htmlFor="contact-email">Email *</Label>
          <input id="contact-email" data-testid="contact-email" required type="email" className={inputCls} placeholder="you@email.com" value={form.email} onChange={set("email")} />
        </div>
        <div>
          <Label htmlFor="contact-phone">Phone number (optional)</Label>
          <input id="contact-phone" data-testid="contact-phone" className={inputCls} placeholder="(555) 123-4567" value={form.phone} onChange={set("phone")} />
        </div>
        <div>
          <Label htmlFor="contact-type">Inquiry type</Label>
          <select id="contact-type" data-testid="contact-type" className={inputCls} value={form.inquiry_type} onChange={set("inquiry_type")}>
            <option>Selling Land</option>
            <option>Buying Land</option>
            <option>General Question</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="contact-message">Message *</Label>
          <textarea id="contact-message" data-testid="contact-message" required rows={5} className={inputCls} placeholder="Tell us how we can help…" value={form.message} onChange={set("message")} />
        </div>
      </div>
      <GoldButton type="submit" data-testid="contact-form-submit" disabled={sending} className="mt-8 w-full sm:w-auto">
        {sending ? "Sending…" : "Send Message"} <Send size={15} />
      </GoldButton>
    </form>
  );
};

const Contact = () => {
  usePageTitle("Contact | Aspira Property Group");
  return (
    <section data-testid="contact-page" className="relative overflow-hidden">
      <div className="pointer-events-none absolute -left-40 top-0 h-[420px] w-[420px] rounded-full bg-aspira-gold/10 blur-3xl" />
      <div className="mx-auto max-w-7xl px-5 pb-24 pt-16 sm:px-8 lg:pt-24">
        <div className="grid items-start gap-14 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="lg:sticky lg:top-28">
            <FadeIn><Eyebrow>Contact</Eyebrow></FadeIn>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-aspira-ink sm:text-5xl">
              <HeroLine delay={0.15}>Let's Talk</HeroLine>
              <HeroLine delay={0.3}>
                <em className="italic text-aspira-gold">Land.</em>
              </HeroLine>
            </h1>
            <FadeIn delay={0.5}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-aspira-body">
                Have a property to sell, a question about an available listing, or
                an opportunity you'd like to discuss? We'd be happy to hear from
                you.
              </p>
            </FadeIn>
            <div className="mt-10 space-y-4">
              <Reveal>
                <a
                  href={CONTACT.phoneHref}
                  data-testid="contact-phone-link"
                  className="group flex items-center gap-4 rounded-2xl border border-aspira-goldborder/60 bg-white p-5 transition-all duration-300 hover:border-aspira-gold hover:shadow-lg"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-aspira-goldlight text-aspira-gold transition-colors duration-300 group-hover:bg-aspira-gold group-hover:text-white">
                    <Phone size={19} />
                  </span>
                  <span>
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-aspira-muted">Call us</span>
                    <span className="mt-0.5 block text-sm font-semibold text-aspira-ink">{CONTACT.phone}</span>
                  </span>
                </a>
              </Reveal>
              <Reveal delay={0.08}>
                <a
                  href={CONTACT.emailHref}
                  data-testid="contact-email-link"
                  className="group flex items-center gap-4 rounded-2xl border border-aspira-goldborder/60 bg-white p-5 transition-all duration-300 hover:border-aspira-gold hover:shadow-lg"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-aspira-goldlight text-aspira-gold transition-colors duration-300 group-hover:bg-aspira-gold group-hover:text-white">
                    <Mail size={19} />
                  </span>
                  <span>
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-aspira-muted">Email us</span>
                    <span className="mt-0.5 block text-sm font-semibold text-aspira-ink">{CONTACT.email}</span>
                  </span>
                </a>
              </Reveal>
            </div>
          </div>
          <FadeIn delay={0.3}>
            <ContactForm />
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

export default Contact;
